<template>
  <div class="w-full">
    <div
      class="flex flex-col rounded-lg border border-[#DA1E28] bg-[#DA1E28] text-white w-full overflow-hidden transition-all duration-300 select-none cursor-pointer"
      role="alert"
      :aria-expanded="alertExpanded"
      @click="alertExpanded = !alertExpanded"
    >
      <div class="flex items-center justify-between px-4 py-3 gap-[10px]">
        <div class="flex items-center gap-[10px] flex-[1_0_0]">
          <div v-if="icon" class="shrink-0 flex items-center justify-center">
            <v-icon :icon="$props.icon" />
          </div>

          <span class="text-white font-['Inter'] text-base font-bold leading-6 tracking-[0.08px]">
            {{ title }}
          </span>
        </div>

        <div
          class="shrink-0 transition-transform duration-[250ms] font-bold text-[10px]"
          :class="alertExpanded ? 'rotate-180' : 'rotate-0'"
        >
          ▼
        </div>
      </div>

      <div
        class="h-px bg-white/25 mx-3 origin-left transition-[transform,opacity] duration-200 ease-[ease] delay-[0.05s]"
        :class="alertExpanded ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'"
      />

      <div
        class="overflow-hidden transition-[max-height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        :class="alertExpanded ? 'max-h-[200px]' : 'max-h-0'"
      >
        <div
          class="px-4 pt-[10px] pb-[14px] flex flex-row items-start gap-[10px] transition-[opacity,transform] duration-[220ms] ease-[ease] delay-[0.08s]"
          :class="alertExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'"
        >
          <div v-if="icon" class="shrink-0 flex items-center justify-center scale-90 pt-1">
            <v-icon :icon="$props.icon" />
          </div>

          <div class="flex flex-col items-start w-full">
            <div class="flex items-center gap-[10px] w-full justify-start mb-2">
              <span class="text-white font-semibold text-sm tracking-[0.01em] opacity-[0.88]">
                {{ title }}
              </span>
            </div>

            <div
              class="flex items-center justify-start rounded-lg px-[8px] pt-[3px] pb-[4px] w-full border border-white/10 backdrop-blur-sm bg-[#FCE9EA]/[0.24] text-center"
            >
              <p
                class="m-0 text-white font-['Inter'] text-[12.5px] font-medium leading-[1.5] tracking-[0.01em]"
              >
                <slot>
                  <template v-if="status">
                    Folha de monitoramento com pendência: {{ statusLabel }}.
                  </template>
                  <template v-else>{{ noRecordMessage }}</template>
                </slot>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, Component, ref, computed } from 'vue'
import {
  DSAttendanceStatus,
  DS_ATTENDANCE_STATUS_LABELS
} from '@/constants/ds-attendance.constants'

export default defineComponent({
  name: 'NoShiftRecordAlert',
  props: {
    icon: {
      type: [Object, Function, String] as PropType<Component | string>,
      default: null
    },
    iconProps: {
      type: Object,
      default: () => ({ color: 'white', width: '22', height: '22' })
    },
    title: {
      // O título é repetido no cabeçalho e no corpo do accordion; usando a mesma
      // frase da mensagem detalhada, o alerta dizia a mesma coisa três vezes.
      type: String,
      default: 'Registro de plantão pendente'
    },
    date: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    isOverdue: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const alertExpanded = ref(false)
    // `status` é tipado como `string` livre no endpoint, então um valor novo do
    // backend cairia cru dentro de uma frase em pt-BR.
    const statusLabel = computed(() => {
      return (
        DS_ATTENDANCE_STATUS_LABELS[props.status as DSAttendanceStatus] || 'status não reconhecido'
      )
    })
    // `date` pode chegar vazia (quando `formatDate` do modal devolve null), o que
    // produzia a frase truncada "...aprovado para .".
    const noRecordMessage = computed(() =>
      props.date
        ? `Nenhum registro de plantão aprovado para ${props.date}.`
        : 'Nenhum registro de plantão aprovado.'
    )
    return { alertExpanded, statusLabel, noRecordMessage }
  }
})
</script>
